import { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import PageHeader from '../components/PageHeader';
import StatCard from '../components/StatCard';

const STATUS_STYLE = {
  complete:    { background: 'var(--gr-l)', color: 'var(--gr)',  border: '1px solid var(--gr-b)' },
  scheduled:   { background: 'var(--bl-l)', color: 'var(--bl)',  border: '1px solid var(--bl-b)' },
  in_progress: { background: 'var(--am-l)', color: 'var(--am)',  border: '1px solid var(--am-b)' },
};
const STATUS_LABEL = { complete: 'Complete', scheduled: 'Scheduled', in_progress: 'In Progress' };

export default function MaintenancePage() {
  const { vehicles, maintHist, updateVehicle, openModal, showToast, addMaintRecord, updateMaintRecord, canEdit } = useApp();

  // Main tabs
  const [mainTab, setMainTab] = useState('fleet');
  const [fleetSearch, setFleetSearch] = useState('');
  const [sortCol, setSortCol] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [expandedVeh, setExpandedVeh] = useState(null);

  // Complete service inline form
  const [completing, setCompleting] = useState(null); // maintHist record id
  const [compForm, setCompForm] = useState({ date: '', odo: '', cost: '', garage: '', nextKm: '', nextDate: '', notes: '' });

  // History tab state
  const [histTab, setHistTab]       = useState('all');
  const [histVeh, setHistVeh]       = useState('');
  const [histType, setHistType]     = useState('');
  const [histDateFrom, setHistDateFrom] = useState('');
  const [histDateTo, setHistDateTo]     = useState('');

  // ── Stats ──
  const scheduledCount = maintHist.filter(m => m.status === 'scheduled').length;
  const inProgressCount = maintHist.filter(m => m.status === 'in_progress').length;
  const completedCount = maintHist.filter(m => m.status === 'complete').length;
  const totalCost      = maintHist.filter(m => m.cost).reduce((s, m) => s + m.cost, 0);

  // ── Fleet overview data ──
  const fleetRows = useMemo(() => {
    return vehicles.map(v => {
      const vehHist = maintHist.filter(m => m.vid === v.id);
      const scheduled = vehHist.filter(m => m.status === 'scheduled');
      const inProgress = vehHist.filter(m => m.status === 'in_progress');
      const completed = vehHist.filter(m => m.status === 'complete');

      // Next service: nearest scheduled or in_progress by KM
      const nextByKm = [...scheduled, ...inProgress]
        .filter(m => m.nextKm)
        .sort((a, b) => a.nextKm - b.nextKm)[0] || null;

      // Next service by date
      const nextByDate = [...scheduled, ...inProgress]
        .filter(m => m.nextDate)
        .sort((a, b) => new Date(a.nextDate) - new Date(b.nextDate))[0] || null;

      // Remaining KM to next service
      const odo = v.odometer || 0;
      let remainingKm = null;
      let nextServiceLabel = '—';
      if (nextByKm) {
        remainingKm = nextByKm.nextKm - odo;
        nextServiceLabel = `${nextByKm.nextKm.toLocaleString()} km · ${nextByKm.type}`;
      }

      // MOT status from vehicle.motExpiry
      let motStatus = 'ok';
      const motExpiry = v.motExpiry || null;
      if (motExpiry) {
        const now = new Date();
        const mot = new Date(motExpiry);
        const diffDays = Math.floor((mot - now) / (1000 * 60 * 60 * 24));
        if (diffDays < 0) motStatus = 'expired';
        else if (diffDays <= 30) motStatus = 'warning';
      }

      // Is in maintenance?
      const isInMaint = inProgress.length > 0;

      return {
        ...v,
        odo,
        vehHist,
        scheduled,
        inProgress,
        completed,
        nextByKm,
        nextByDate,
        remainingKm,
        nextServiceLabel,
        motStatus,
        motExpiry,
        isInMaint,
      };
    });
  }, [vehicles, maintHist]);

  // Filter + sort
  const sortedFleet = useMemo(() => {
    let rows = [...fleetRows];
    if (fleetSearch) {
      const q = fleetSearch.toLowerCase();
      rows = rows.filter(r => r.name.toLowerCase().includes(q) || r.plate?.toLowerCase().includes(q));
    }
    rows.sort((a, b) => {
      let cmp = 0;
      switch (sortCol) {
        case 'name': cmp = a.name.localeCompare(b.name); break;
        case 'km': cmp = a.odo - b.odo; break;
        case 'remaining': cmp = (a.remainingKm ?? 999999) - (b.remainingKm ?? 999999); break;
        case 'mot': {
          const ma = a.motExpiry ? new Date(a.motExpiry).getTime() : Infinity;
          const mb = b.motExpiry ? new Date(b.motExpiry).getTime() : Infinity;
          cmp = ma - mb;
          break;
        }
        default: cmp = 0;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [fleetRows, fleetSearch, sortCol, sortDir]);

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  function SortIcon({ col }) {
    if (sortCol !== col) return <span style={{ opacity: 0.3, fontSize: 9 }}> ↕</span>;
    return <span style={{ fontSize: 9 }}> {sortDir === 'asc' ? '↑' : '↓'}</span>;
  }

  // ── Complete scheduled service ──
  function startComplete(record) {
    setCompleting(record.id);
    setCompForm({
      date: new Date().toISOString().slice(0, 10),
      odo: record.odometerKm?.toString() || '',
      cost: '',
      garage: record.garage || '',
      nextKm: '',
      nextDate: '',
      notes: record.notes || '',
    });
  }

  function submitComplete(record) {
    const veh = vehicles.find(v => v.id === record.vid);
    const newOdo = compForm.odo ? parseInt(compForm.odo) : null;

    // Update the record to complete
    updateMaintRecord(record.id, {
      status: 'complete',
      date: compForm.date ? new Date(compForm.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : record.date,
      km: newOdo ? `${newOdo.toLocaleString()} km` : record.km,
      odometerKm: newOdo || record.odometerKm,
      cost: compForm.cost ? parseFloat(compForm.cost) : record.cost,
      garage: compForm.garage || record.garage,
      notes: compForm.notes || record.notes,
    });

    // Update vehicle odometer if new reading provided
    if (newOdo && veh) {
      updateVehicle(veh.id, { odometer: newOdo });
    }

    // If next KM provided, create a new scheduled record
    if (compForm.nextKm) {
      addMaintRecord({
        id: 'MS-' + Date.now(),
        vid: record.vid,
        icon: veh?.icon || '🔧',
        veh: veh?.name || record.veh,
        type: record.type,
        date: '',
        km: '',
        odometerKm: null,
        cost: null,
        garage: '',
        next: `${parseInt(compForm.nextKm).toLocaleString()} km`,
        nextKm: parseInt(compForm.nextKm),
        triggerType: 'km',
        notifyAt: null,
        notifNote: '',
        notes: '',
        status: 'scheduled',
        notified: false,
      });
    }

    // If next MOT date provided, create a new scheduled record
    if (compForm.nextDate && record.type?.toLowerCase().includes('mot')) {
      addMaintRecord({
        id: 'MS-' + Date.now() + 1,
        vid: record.vid,
        icon: veh?.icon || '🔧',
        veh: veh?.name || record.veh,
        type: 'MOT',
        date: '',
        km: '',
        odometerKm: null,
        cost: null,
        garage: '',
        next: new Date(compForm.nextDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
        nextDate: compForm.nextDate,
        triggerType: 'date',
        notifyAt: null,
        notifNote: '',
        notes: '',
        status: 'scheduled',
        notified: false,
      });
      // Update vehicle MOT expiry
      if (veh) updateVehicle(veh.id, { motExpiry: compForm.nextDate });
    }

    setCompleting(null);
    showToast(`✓ ${record.type} completed for ${veh?.name || record.veh}`);
  }

  // ── History filters ──
  const serviceTypes = useMemo(() => {
    const set = new Set(maintHist.map(m => m.type));
    return [...set].sort();
  }, [maintHist]);

  const historyRecords = useMemo(() => {
    return maintHist.filter(m => {
      if (histTab !== 'all' && m.status !== histTab) return false;
      if (histVeh && m.vid !== histVeh) return false;
      if (histType && m.type !== histType) return false;
      if (histDateFrom || histDateTo) {
        const mDate = parseDate(m.date);
        if (mDate) {
          if (histDateFrom && mDate < new Date(histDateFrom)) return false;
          if (histDateTo && mDate > new Date(histDateTo + 'T23:59:59')) return false;
        } else if (histDateFrom || histDateTo) return false;
      }
      return true;
    });
  }, [maintHist, histTab, histVeh, histType, histDateFrom, histDateTo]);

  const filteredCost = historyRecords.filter(m => m.cost).reduce((s, m) => s + m.cost, 0);

  function exportCSV() {
    const headers = ['Vehicle', 'Type', 'Date', 'KM/Hours', 'Cost', 'Garage', 'Next Due', 'Status', 'Notes'];
    const rows = historyRecords.map(m => [
      m.veh, m.type, m.date || '', m.km || '', m.cost || '', m.garage || '', m.next || '', m.status, m.notes || ''
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'maintenance_history.csv';
    a.click(); URL.revokeObjectURL(url);
  }

  // ── Helpers ──
  function remainingStyle(km) {
    if (km === null || km === undefined) return {};
    if (km <= 0) return { color: 'var(--ac)', fontWeight: 700 };
    if (km <= 2000) return { color: 'var(--am)', fontWeight: 600 };
    return { color: 'var(--gr)' };
  }

  function motBadgeStyle(status) {
    if (status === 'expired') return { background: 'var(--ac-l)', color: 'var(--ac)', border: '1px solid var(--ac-b)' };
    if (status === 'warning') return { background: 'var(--am-l)', color: 'var(--am)', border: '1px solid var(--am-b)' };
    return { background: 'var(--gr-l)', color: 'var(--gr)', border: '1px solid var(--gr-b)' };
  }

  return (
    <div>
      <PageHeader
        title="Maintenance"
        icon="🔧"
        subtitle="Schedule services, track maintenance history, and manage vehicles in the workshop."
        actions={canEdit && canEdit('maintenance') && (
          <button className="btn btn-primary" onClick={() => openModal('add-maintenance')}>+ Log Service</button>
        )}
      />
      {/* Stats — clickable: jumps to Service History tab pre-filtered */}
      <div className="sg sg-4" style={{ marginBottom: 14 }}>
        <StatCard
          label="Scheduled" value={scheduledCount}
          desc={mainTab === 'history' && histTab === 'scheduled' ? 'Filtering — click to clear' : 'Upcoming services'}
          active={mainTab === 'history' && histTab === 'scheduled'}
          onClick={() => {
            if (mainTab === 'history' && histTab === 'scheduled') { setHistTab('all'); }
            else { setMainTab('history'); setHistTab('scheduled'); }
          }}
        />
        <StatCard
          label="In Progress" value={inProgressCount} variant="am"
          desc={mainTab === 'history' && histTab === 'in_progress' ? 'Filtering — click to clear' : 'Vehicles in service'}
          active={mainTab === 'history' && histTab === 'in_progress'}
          onClick={() => {
            if (mainTab === 'history' && histTab === 'in_progress') { setHistTab('all'); }
            else { setMainTab('history'); setHistTab('in_progress'); }
          }}
        />
        <StatCard
          label="Completed YTD" value={completedCount} variant="gr"
          desc={mainTab === 'history' && histTab === 'complete' ? 'Filtering — click to clear' : 'Services done'}
          active={mainTab === 'history' && histTab === 'complete'}
          onClick={() => {
            if (mainTab === 'history' && histTab === 'complete') { setHistTab('all'); }
            else { setMainTab('history'); setHistTab('complete'); }
          }}
        />
        <StatCard label="Total Cost YTD" value={`€${totalCost.toLocaleString()}`} desc="Maintenance spend" />
      </div>

      {/* Main tabs */}
      <div style={{ display: 'flex', gap: 0, background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 7, overflow: 'hidden', marginBottom: 14, width: 'fit-content' }}>
        {[['fleet', '🚗 Fleet Overview'], ['history', '📋 Service History']].map(([val, lbl]) => (
          <button key={val} className={`vb${mainTab === val ? ' active' : ''}`}
            style={{ padding: '8px 20px', fontSize: 12 }}
            onClick={() => setMainTab(val)}>{lbl}</button>
        ))}
      </div>

      {/* ═══════════ TAB 1: Fleet Overview ═══════════ */}
      {mainTab === 'fleet' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            <input type="text" className="fi" placeholder="🔍 Search vehicles…" value={fleetSearch}
              onChange={e => setFleetSearch(e.target.value)}
              style={{ fontSize: 11, padding: '7px 10px', width: 240 }} />
            <div style={{ marginLeft: 'auto' }} />
            <button className="btn btn-primary btn-sm" onClick={() => openModal('add-maintenance')}>+ Log Service</button>
          </div>

          {/* Fleet table */}
          <div style={{ background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 'var(--rl)', overflow: 'hidden' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '2fr .9fr 1.4fr 1fr 1fr .5fr',
              padding: '10px 14px',
              background: 'var(--sur2)',
              borderBottom: '1px solid var(--bd)',
              fontSize: 10, fontWeight: 600, color: 'var(--tx3)',
              textTransform: 'uppercase', letterSpacing: '.04em',
              cursor: 'pointer', userSelect: 'none',
            }}>
              <div onClick={() => toggleSort('name')}>Vehicle<SortIcon col="name" /></div>
              <div onClick={() => toggleSort('km')}>Odometer<SortIcon col="km" /></div>
              <div onClick={() => toggleSort('remaining')}>Next Service<SortIcon col="remaining" /></div>
              <div onClick={() => toggleSort('remaining')}>Remaining<SortIcon col="remaining" /></div>
              <div onClick={() => toggleSort('mot')}>MOT Expiry<SortIcon col="mot" /></div>
              <div></div>
            </div>

            {sortedFleet.length === 0 && (
              <div style={{ padding: 30, textAlign: 'center', fontSize: 12, color: 'var(--tx3)' }}>No vehicles found</div>
            )}

            {sortedFleet.map((row, idx) => {
              const isExpanded = expandedVeh === row.id;
              const motText = row.motExpiry
                ? new Date(row.motExpiry).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                : '—';

              return (
                <div key={row.id}>
                  <div
                    onClick={() => setExpandedVeh(isExpanded ? null : row.id)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr .9fr 1.4fr 1fr 1fr .5fr',
                      padding: '10px 14px',
                      alignItems: 'center',
                      cursor: 'pointer',
                      borderBottom: '1px solid var(--bd)',
                      background: row.isInMaint ? 'rgba(184,114,10,.06)' : idx % 2 === 0 ? 'var(--sur)' : 'var(--sur2)',
                      transition: 'background .15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bl-l)'}
                    onMouseLeave={e => e.currentTarget.style.background = row.isInMaint ? 'rgba(184,114,10,.06)' : idx % 2 === 0 ? 'var(--sur)' : 'var(--sur2)'}
                  >
                    {/* Vehicle */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 18 }}>{row.icon}</span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                          {row.name}
                          {row.isInMaint && (
                            <span style={{ fontSize: 8, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: 'var(--am-l)', color: 'var(--am)' }}>
                              🔧 IN SERVICE
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 9, color: 'var(--tx3)', fontFamily: "'DM Mono',monospace" }}>
                          {row.plate || ''} · {row.type || ''}
                        </div>
                      </div>
                    </div>

                    {/* Odometer */}
                    <div style={{ fontSize: 12, fontFamily: "'DM Mono',monospace", fontWeight: 500 }}>
                      {row.odo.toLocaleString()} <span style={{ fontSize: 9, color: 'var(--tx3)' }}>km</span>
                    </div>

                    {/* Next service */}
                    <div style={{ fontSize: 11 }}>{row.nextServiceLabel}</div>

                    {/* Remaining */}
                    <div style={{ fontSize: 12, fontFamily: "'DM Mono',monospace", fontWeight: 600, ...remainingStyle(row.remainingKm) }}>
                      {row.remainingKm !== null ? `${row.remainingKm.toLocaleString()} km` : '—'}
                    </div>

                    {/* MOT */}
                    <div>
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 4,
                        ...motBadgeStyle(row.motStatus),
                      }}>
                        {motText}
                      </span>
                    </div>

                    {/* Expand arrow */}
                    <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--tx3)' }}>
                      {isExpanded ? '▲' : '▼'}
                    </div>
                  </div>

                  {/* ── Expanded section ── */}
                  {isExpanded && (
                    <div style={{ padding: '14px 20px', background: 'var(--bg)', borderBottom: '1px solid var(--bd)' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                        {/* Left: Scheduled & In-Progress services */}
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
                            Pending Services
                          </div>

                          {row.scheduled.length === 0 && row.inProgress.length === 0 && (
                            <div style={{ fontSize: 11, color: 'var(--tx3)', padding: '8px 0' }}>No pending services</div>
                          )}

                          {[...row.inProgress, ...row.scheduled].map(m => (
                            <div key={m.id} style={{
                              padding: '8px 10px', marginBottom: 6,
                              background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 'var(--r)',
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                <span style={{
                                  fontSize: 8, fontWeight: 600, padding: '2px 6px', borderRadius: 4, flexShrink: 0,
                                  ...STATUS_STYLE[m.status],
                                }}>
                                  {STATUS_LABEL[m.status]}
                                </span>
                                <span style={{ fontSize: 11, fontWeight: 600, flex: 1 }}>{m.type}</span>
                                {m.next && <span style={{ fontSize: 10, color: 'var(--am)', fontFamily: "'DM Mono',monospace" }}>Due: {m.next}</span>}
                              </div>
                              {m.notes && <div style={{ fontSize: 10, color: 'var(--tx3)', marginBottom: 4 }}>{m.notes}</div>}

                              {/* Complete button or form */}
                              {completing === m.id ? (
                                <div style={{ marginTop: 6, padding: '8px 10px', background: 'var(--sur2)', borderRadius: 'var(--r)', border: '1px solid var(--bd)' }}>
                                  <div style={{ fontSize: 10, fontWeight: 600, marginBottom: 6 }}>Complete Service</div>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                                    <div>
                                      <label style={{ fontSize: 9, color: 'var(--tx3)' }}>Date</label>
                                      <input type="date" className="fi" value={compForm.date}
                                        onChange={e => setCompForm(f => ({ ...f, date: e.target.value }))}
                                        style={{ fontSize: 10, padding: '4px 6px' }} />
                                    </div>
                                    <div>
                                      <label style={{ fontSize: 9, color: 'var(--tx3)' }}>Odometer (km)</label>
                                      <div style={{ fontSize: 10, padding: '4px 6px', background: '#f5f5f5', borderRadius: 6, border: '1px solid #e0e0e0', color: '#888' }}>
                                        {row.odo.toLocaleString()} km
                                      </div>
                                    </div>
                                    <div>
                                      <label style={{ fontSize: 9, color: 'var(--tx3)' }}>Cost (€)</label>
                                      <input type="number" className="fi" value={compForm.cost}
                                        onChange={e => setCompForm(f => ({ ...f, cost: e.target.value }))}
                                        style={{ fontSize: 10, padding: '4px 6px' }} />
                                    </div>
                                    <div>
                                      <label style={{ fontSize: 9, color: 'var(--tx3)' }}>Garage</label>
                                      <input type="text" className="fi" value={compForm.garage}
                                        onChange={e => setCompForm(f => ({ ...f, garage: e.target.value }))}
                                        style={{ fontSize: 10, padding: '4px 6px' }} />
                                    </div>
                                    <div>
                                      <label style={{ fontSize: 9, color: 'var(--tx3)' }}>Next service at (km)</label>
                                      <input type="number" className="fi" value={compForm.nextKm}
                                        onChange={e => setCompForm(f => ({ ...f, nextKm: e.target.value }))}
                                        placeholder="e.g. 140000"
                                        style={{ fontSize: 10, padding: '4px 6px' }} />
                                    </div>
                                    {m.type?.toLowerCase().includes('mot') && (
                                      <div>
                                        <label style={{ fontSize: 9, color: 'var(--tx3)' }}>Next MOT date</label>
                                        <input type="date" className="fi" value={compForm.nextDate}
                                          onChange={e => setCompForm(f => ({ ...f, nextDate: e.target.value }))}
                                          style={{ fontSize: 10, padding: '4px 6px' }} />
                                      </div>
                                    )}
                                  </div>
                                  <div>
                                    <label style={{ fontSize: 9, color: 'var(--tx3)' }}>Notes</label>
                                    <textarea className="fi" value={compForm.notes}
                                      onChange={e => setCompForm(f => ({ ...f, notes: e.target.value }))}
                                      rows={2} style={{ fontSize: 10, padding: '4px 6px' }} />
                                  </div>
                                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                    <button className="btn btn-primary btn-sm" onClick={() => submitComplete(m)}>
                                      ✓ Mark Complete
                                    </button>
                                    <button className="btn btn-ghost btn-sm" onClick={() => setCompleting(null)}>Cancel</button>
                                  </div>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                                  <button className="btn btn-primary btn-sm" style={{ fontSize: 10, padding: '4px 10px' }}
                                    onClick={e => { e.stopPropagation(); startComplete(m); }}>
                                    ✓ Complete
                                  </button>
                                  {m.status === 'scheduled' && (
                                    <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: '4px 10px', color: 'var(--am)' }}
                                      onClick={e => {
                                        e.stopPropagation();
                                        updateMaintRecord(m.id, { status: 'in_progress' });
                                        showToast(`🔧 ${m.type} now in progress — vehicle unavailable for booking`);
                                      }}>
                                      🔧 Start Work
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}

                        </div>

                        {/* Right: Recent completed services */}
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
                            Recent Completed
                          </div>
                          {row.completed.length === 0 && (
                            <div style={{ fontSize: 11, color: 'var(--tx3)', padding: '8px 0' }}>No completed services</div>
                          )}
                          {row.completed.slice(0, 5).map(m => (
                            <div key={m.id} style={{
                              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
                              borderBottom: '1px solid var(--bd)',
                            }}>
                              <span style={{ fontSize: 11, fontWeight: 500, flex: 1 }}>{m.type}</span>
                              <span style={{ fontSize: 10, color: 'var(--tx3)', fontFamily: "'DM Mono',monospace" }}>
                                {m.date || '—'}
                              </span>
                              <span style={{ fontSize: 10, fontFamily: "'DM Mono',monospace" }}>
                                {m.km || '—'}
                              </span>
                              {m.cost != null && (
                                <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx)' }}>€{m.cost}</span>
                              )}
                            </div>
                          ))}
                          {row.completed.length > 5 && (
                            <div style={{ fontSize: 10, color: 'var(--tx3)', padding: '6px 0' }}>
                              + {row.completed.length - 5} more
                            </div>
                          )}

                          {/* Actions */}
                          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                            <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); openModal('add-maintenance', row.id); }}>
                              🔧 Log Service
                            </button>
                            <button className="btn btn-ghost btn-sm" onClick={e => {
                              e.stopPropagation();
                              setMainTab('history');
                              setHistVeh(row.id);
                            }}>
                              📋 Full History
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Bottom info */}
                      <div style={{ display: 'flex', gap: 20, marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--bd)' }}>
                        <div style={{ fontSize: 10, color: 'var(--tx3)' }}>
                          MOT: <strong style={{ color: row.motStatus === 'expired' ? 'var(--ac)' : row.motStatus === 'warning' ? 'var(--am)' : 'var(--gr)' }}>
                            {motLabel(row.motExpiry)}
                          </strong>
                          {row.motStatus === 'expired' && <span style={{ color: 'var(--ac)', fontWeight: 700 }}> · EXPIRED</span>}
                          {row.motStatus === 'warning' && <span style={{ color: 'var(--am)', fontWeight: 600 }}> · Expiring soon</span>}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--tx3)' }}>
                          Total spent: <strong style={{ color: 'var(--tx)' }}>€{row.vehHist.filter(m => m.cost).reduce((s, m) => s + m.cost, 0).toLocaleString()}</strong>
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--tx3)' }}>
                          Services: <strong style={{ color: 'var(--tx)' }}>{row.completed.length} completed</strong>
                          {row.scheduled.length > 0 && <>, <strong style={{ color: 'var(--bl)' }}>{row.scheduled.length} scheduled</strong></>}
                          {row.inProgress.length > 0 && <>, <strong style={{ color: 'var(--am)' }}>{row.inProgress.length} in progress</strong></>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ═══════════ TAB 2: Service History ═══════════ */}
      {mainTab === 'history' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 0, background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 7, overflow: 'hidden' }}>
              {[['all', 'All'], ['scheduled', 'Scheduled'], ['in_progress', 'In Progress'], ['complete', 'Complete']].map(([val, lbl]) => (
                <button key={val} className={`vb${histTab === val ? ' active' : ''}`} onClick={() => setHistTab(val)}>{lbl}</button>
              ))}
            </div>
            <select className="fsel" value={histVeh} onChange={e => setHistVeh(e.target.value)} style={{ fontSize: 11 }}>
              <option value="">All vehicles</option>
              {vehicles.map(v => <option key={v.id} value={v.id}>{v.icon} {v.name}</option>)}
            </select>
            <select className="fsel" value={histType} onChange={e => setHistType(e.target.value)} style={{ fontSize: 11 }}>
              <option value="">All types</option>
              {serviceTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="date" className="fi" value={histDateFrom} onChange={e => setHistDateFrom(e.target.value)}
                style={{ fontSize: 10, padding: '5px 6px', width: 120 }} />
              <span style={{ fontSize: 10, color: 'var(--tx3)' }}>→</span>
              <input type="date" className="fi" value={histDateTo} onChange={e => setHistDateTo(e.target.value)}
                style={{ fontSize: 10, padding: '5px 6px', width: 120 }} />
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <button className="btn btn-ghost btn-sm" onClick={exportCSV}>📥 Export CSV</button>
              <button className="btn btn-primary btn-sm" onClick={() => openModal('add-maintenance')}>+ Log Service</button>
            </div>
          </div>

          <div style={{
            display: 'flex', gap: 20, marginBottom: 12, padding: '8px 14px',
            background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 'var(--r)', fontSize: 11,
          }}>
            <span style={{ color: 'var(--tx3)' }}>Showing <strong style={{ color: 'var(--tx)' }}>{historyRecords.length}</strong> records</span>
            <span style={{ color: 'var(--tx3)' }}>Total cost: <strong style={{ color: 'var(--gr)' }}>€{filteredCost.toLocaleString()}</strong></span>
          </div>

          <div className="tbl">
            <div className="tbl-hd" style={{ gridTemplateColumns: '1.4fr 1fr .8fr .8fr .7fr 1fr .8fr .6fr' }}>
              <div className="th">Vehicle</div>
              <div className="th">Type</div>
              <div className="th">Date</div>
              <div className="th">KM / Hours</div>
              <div className="th">Cost</div>
              <div className="th">Garage</div>
              <div className="th">Next Due</div>
              <div className="th">Status</div>
            </div>

            {historyRecords.length === 0 && (
              <div style={{ padding: 30, textAlign: 'center', fontSize: 12, color: 'var(--tx3)' }}>No records match your filters</div>
            )}

            {historyRecords.map(m => (
              <div key={m.id} className="tr" style={{ gridTemplateColumns: '1.4fr 1fr .8fr .8fr .7fr 1fr .8fr .6fr' }}
                onClick={() => showToast(`${m.type} — ${m.veh}${m.notes ? '\n' + m.notes : ''}`)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 14 }}>{m.icon}</span>
                  <span style={{ fontSize: 11, fontWeight: 500 }}>{m.veh}</span>
                </div>
                <div><span className="sp s-mt">{m.type}</span></div>
                <div className="dv">{m.date || '—'}</div>
                <div style={{ fontSize: 10, fontFamily: "'DM Mono',monospace" }}>{m.km || '—'}</div>
                <div>
                  {m.cost != null
                    ? <span className="pv">€{m.cost}</span>
                    : <span style={{ fontSize: 10, color: 'var(--tx3)' }}>—</span>
                  }
                </div>
                <div style={{ fontSize: 10, color: 'var(--tx2)' }}>{m.garage || '—'}</div>
                <div style={{ fontSize: 10, color: 'var(--am)', fontFamily: "'DM Mono',monospace" }}>{m.next || '—'}</div>
                <div>
                  <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 4, ...STATUS_STYLE[m.status] }}>
                    {STATUS_LABEL[m.status] || m.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helpers ──
function motLabel(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function parseDate(str) {
  if (!str) return null;
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}
